import React, { useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import './SystemBanner.css';

const DEFAULT_DURATION = 6000;

// Kind → icon mapping. The "info" default tracks the design's sparkle accent.
const ICON_FOR_KIND = {
  info: 'sparkle',
  join: 'check',
  leave: 'logout',
  night: 'moon',
};

/**
 * SystemBanner — a centered glass pill that fades in, sits for ~6s, then fades
 * out. Used by the chat region to mark room events ("Welcome to GLOB",
 * "kira joined", "Welcome to night mode").
 *
 * Props:
 *   - kind: 'info' | 'join' | 'leave' | 'night'
 *   - text: string
 *   - duration?: number — ms before auto-dismiss; 0 means never
 *   - onDismiss?: () => void — called after the leave animation finishes
 */
function SystemBanner({
  kind = 'info',
  text,
  duration = DEFAULT_DURATION,
  onDismiss,
}) {
  const [phase, setPhase] = useState('in'); // 'in' | 'leaving' | 'gone'

  useEffect(() => {
    if (!duration || duration <= 0) return undefined;
    const timer = setTimeout(() => setPhase('leaving'), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  useEffect(() => {
    if (phase !== 'leaving') return undefined;
    const timer = setTimeout(() => {
      setPhase('gone');
      if (onDismiss) onDismiss();
    }, 220);
    return () => clearTimeout(timer);
  }, [phase, onDismiss]);

  if (phase === 'gone') return null;

  const iconName = ICON_FOR_KIND[kind] || 'sparkle';
  const className =
    `glacier-sys-banner ${kind}` +
    (phase === 'leaving' ? ' glacier-sys-banner--leaving' : '');

  return (
    <div
      className={className}
      role="status"
      data-testid="system-banner"
      data-kind={kind}
    >
      <span className="glacier-sys-banner__icon">
        <Icon name={iconName} size={12} aria-hidden />
      </span>
      <span>{text}</span>
    </div>
  );
}

export default SystemBanner;
